import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Modal, Card, Row, Col, Spin, Typography, Progress, Input, Tooltip, Collapse } from 'antd';
import { ThemeContext } from '../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightOutlined,

  SafetyCertificateOutlined,
  LineChartOutlined,
  SlidersOutlined,
  PieChartOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  IdcardOutlined,
  TeamOutlined,
  UserOutlined,
  ShopOutlined,
  BankOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  SearchOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  RadarChartOutlined,
} from '@ant-design/icons';
import LandingLayout from './LandingLayoutRedesign';
import AnimatedYieldCurveChart from './AnimatedYieldCurveChartRedesign';
import StateGovernmentYieldChart from './StateGovernmentYieldChartRedesign';
import AAAYieldChart from './AAAYieldChartRedesign';
import ScanScoreChart from '../shared/ScanScoreChart';
import DefaultRiskPredictor from '../shared/DefaultRiskPredictor';
import TodayIssueCard from './TodayIssueCard';
import BondValuationCard from './BondValuationCard';
import PortfolioBuilderShowcase from './PortfolioBuilderShowcase';
import FbilBenchmarksPanel from './FbilBenchmarksPanel';
import './LandingPageRedesign.css';
import '../LandingPages/InstrumentDetails.css';
import './FbilBenchmarksRedesign.css';
import apiClient from '../../services/apiClient';
import { getDisplayRiskLabel, getDisplayRiskRange } from '../../utils/mertonRiskBands';

const { Text } = Typography;
const { Search } = Input;

const YIELD_CURVE_SLIDES = [
  { key: 'gsec', label: 'G-Sec', Component: AnimatedYieldCurveChart },
  { key: 'sdl', label: 'SDL', Component: StateGovernmentYieldChart },
  { key: 'aaa', label: 'AAA', Component: AAAYieldChart, chartProps: { highlightYear: 10, maxYear: 10 } },
];

const BOND_VALUATION_CARDS_PER_SLIDE = 3;

const RBI_RATE_TAB_ORDER = [
  'policy_rates',
  'reserve_ratios',
  'exchange_rates',
  'lending_deposit_rates',
  'market_trends',
];

const RBI_SECTION_LABELS = {
  policy_rates: 'Policy Rates',
  reserve_ratios: 'Reserve Ratios',
  exchange_rates: 'Exchange Rates',
  lending_deposit_rates: 'Lending / Deposit Rates',
  market_trends: 'Market Trends',
};

const RBI_SUBSECTION_LABELS = {
  money_market: 'Money Market',
  government_securities_market: 'G-Sec Market',
  capital_market: 'Capital Market',
};

const LandingPageRedesign = () => {
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const [issuersData, setIssuersData] = useState([]);
  const [isinsData, setIsinsData] = useState([]);
  const [currentRatesData, setCurrentRatesData] = useState({ sections: [], updatedAt: null });
  const [loadingCurrentRates, setLoadingCurrentRates] = useState(true);
  const [activeRateTab, setActiveRateTab] = useState('policy_rates');
  const [marketCapTickerData, setMarketCapTickerData] = useState([]);
  const [randomIsinData, setRandomIsinData] = useState([]);
  const [loadingRandomIsins, setLoadingRandomIsins] = useState(true);
  const [bondValuations, setBondValuations] = useState([]);
  const [bondValuationsTradeDate, setBondValuationsTradeDate] = useState(null);
  const [loadingBondValuations, setLoadingBondValuations] = useState(true);
  const [activeBondSlideIndex, setActiveBondSlideIndex] = useState(0);
  const [bondSlideDirection, setBondSlideDirection] = useState(1);
  const [bondCarouselHoverPaused, setBondCarouselHoverPaused] = useState(false);
  const [todayIssuesData, setTodayIssuesData] = useState([]);
  const [loadingTodayIssues, setLoadingTodayIssues] = useState(true);
  const [activeIsinIndex, setActiveIsinIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [assessmentCarouselHoverPaused, setAssessmentCarouselHoverPaused] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanModalData, setScanModalData] = useState(null);
  const [scanModalLoading, setScanModalLoading] = useState(false);
  const [scanModalIsin, setScanModalIsin] = useState('');
  const [isinSearchQuery, setIsinSearchQuery] = useState('');
  const [isinSearchLoading, setIsinSearchLoading] = useState(false);
  const [isinSearchError, setIsinSearchError] = useState('');
  const [searchedIsinItem, setSearchedIsinItem] = useState(null);
  const [riskIntelligenceData, setRiskIntelligenceData] = useState([]);
  const [loadingRiskIntelligence, setLoadingRiskIntelligence] = useState(true);
  const [activeRiskIndex, setActiveRiskIndex] = useState(0);
  const [riskSlideDirection, setRiskSlideDirection] = useState(1);
  const [riskCarouselHoverPaused, setRiskCarouselHoverPaused] = useState(false);
  const [riskInfoOpen, setRiskInfoOpen] = useState(false);
  const riskInfoOpenCountRef = useRef(0);
  const riskCarouselPaused = riskCarouselHoverPaused || riskInfoOpen;

  const handleRiskInfoOpenChange = (open) => {
    riskInfoOpenCountRef.current = Math.max(0, riskInfoOpenCountRef.current + (open ? 1 : -1));
    setRiskInfoOpen(riskInfoOpenCountRef.current > 0);
  };
  const [currentTime, setCurrentTime] = useState(new Date());

  // Fetch data
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8083/api/v1/sb';

  const fetchIssuersData = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/master-issuer/all`);
      const result = response.data;
      if (result.statusCode === 200 && result.data && Array.isArray(result.data.content)) {
        setIssuersData(result.data.content);
      } else if (Array.isArray(result)) {
        setIssuersData(result);
      }
    } catch (error) {
      console.error('Error fetching issuers data:', error);
    }
  };

  const fetchIsinsData = async () => {
    try {
      const response = await apiClient.get(`${baseUrl}/master-isin/all`);
      const result = response.data;
      if (result.statusCode === 200 && result.data && Array.isArray(result.data.content)) {
        setIsinsData(result.data.content.slice(0, 5));
      } else if (Array.isArray(result)) {
        setIsinsData(result.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching ISINs data:', error);
    }
  };

  const formatSectionLabel = (section, subsection) => {
    const base = RBI_SECTION_LABELS[section] || (section || '').replace(/_/g, ' ');
    if (subsection) {
      return RBI_SUBSECTION_LABELS[subsection] || subsection.replace(/_/g, ' ');
    }
    return base;
  };

  const fetchCurrentRatesData = async () => {
    try {
      setLoadingCurrentRates(true);
      const response = await apiClient.get(`${baseUrl}/rbi/current-rates`);
      const result = response.data;
      if (result?.statusCode === 200 && result.data) {
        setCurrentRatesData({
          sections: Array.isArray(result.data.sections) ? result.data.sections : [],
          updatedAt: result.data.updatedAt || null,
        });
      } else if (result?.sections) {
        setCurrentRatesData({
          sections: Array.isArray(result.sections) ? result.sections : [],
          updatedAt: result.updatedAt || null,
        });
      }
    } catch (error) {
      console.error('Error fetching RBI Current Rates:', error);
      setCurrentRatesData({ sections: [], updatedAt: null });
    } finally {
      setLoadingCurrentRates(false);
    }
  };

  const currentRatesView = useMemo(() => {
    const sections = currentRatesData.sections || [];
    const bySection = {};
    const marketSubs = [];

    sections.forEach((sec) => {
      const section = sec.section || '';
      if (section === 'market_trends') {
        marketSubs.push(sec);
        return;
      }
      if (!bySection[section]) {
        bySection[section] = sec;
      }
    });

    const tabs = RBI_RATE_TAB_ORDER.filter((id) => {
      if (id === 'market_trends') return marketSubs.length > 0;
      return Boolean(bySection[id]);
    }).map((id) => ({
      id,
      label: RBI_SECTION_LABELS[id] || id.replace(/_/g, ' '),
    }));

    return { bySection, marketSubs, tabs };
  }, [currentRatesData.sections]);

  useEffect(() => {
    if (!currentRatesView.tabs.length) return;
    if (activeRateTab && !currentRatesView.tabs.some((t) => t.id === activeRateTab)) {
      setActiveRateTab(currentRatesView.tabs[0].id);
    }
  }, [currentRatesView.tabs, activeRateTab]);

  const formatCouponRate = (couponRate) => {
    if (couponRate == null || couponRate === '') return '—';
    const raw = String(couponRate).trim();
    return raw.endsWith('%') ? raw : `${raw}%`;
  };

  const parseCouponRateParts = (couponRate) => {
    if (couponRate == null || couponRate === '') {
      return { number: '—', showPercent: false };
    }
    const raw = String(couponRate).trim().replace('%', '').trim();
    return { number: raw || '—', showPercent: raw !== '' };
  };

  const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const formatMaturityDate = (dateStr) => {
    if (dateStr == null || String(dateStr).trim() === '') return '—';

    const trimmed = String(dateStr).trim();
    const spaceIdx = trimmed.indexOf(' ');
    const datePart = spaceIdx > 0 ? trimmed.substring(0, spaceIdx) : trimmed;

    let day;
    let month;
    let year;

    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(datePart)) {
      [year, month, day] = datePart.split('-').map(Number);
    } else {
      const separator = datePart.includes('/') ? '/' : datePart.includes('-') ? '-' : null;
      if (!separator) return trimmed;

      const parts = datePart.split(separator).map(Number);
      if (parts.length !== 3) return trimmed;

      if (parts[0] > 31) {
        [year, month, day] = parts;
      } else {
        [day, month, year] = parts;
      }
    }

    if (!day || !month || !year || month < 1 || month > 12) return trimmed;

    return `${day} ${MONTH_SHORT[month - 1]} ${year}`;
  };

  const goToAssessmentSlide = (idx) => {
    setSlideDirection(idx > activeIsinIndex ? 1 : -1);
    setActiveIsinIndex(idx);
  };

  const assessmentSlideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 48 : -48,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -48 : 48,
      opacity: 0,
    }),
  };

  const riskSlideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 64 : -64,
      opacity: 0,
      scale: 0.98,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -64 : 64,
      opacity: 0,
      scale: 0.98,
    }),
  };

  const fetchRandomIsinData = async () => {
    try {
      setLoadingRandomIsins(true);
      const response = await apiClient.get(`${baseUrl}/master-isin/getrandomisin`);
      const result = response.data;
      let items = [];

      if (result?.statusCode === 200 && Array.isArray(result.data)) {
        items = result.data;
      } else if (Array.isArray(result?.data)) {
        items = result.data;
      } else if (Array.isArray(result)) {
        items = result;
      }

      setRandomIsinData(items.map((item) => ({ ...item, scanScore: null, scanDetails: null })));
      setActiveIsinIndex(0);
      setLoadingRandomIsins(false);

      const itemsWithScan = await Promise.all(
        items.map(async (item) => {
          if (!item?.isin) return { ...item, scanScore: null, scanDetails: null };
          try {
            const scanRes = await apiClient.get(`${baseUrl}/scan/details/${item.isin}`, {
              timeout: 10000,
            });
            const scanScore = scanRes.data?.totalWeightedScore ?? null;
            return { ...item, scanScore, scanDetails: scanRes.data ?? null };
          } catch {
            return { ...item, scanScore: null, scanDetails: null };
          }
        })
      );

      setRandomIsinData(itemsWithScan);
    } catch (error) {
      console.error('Error fetching random ISIN data:', error);
      setRandomIsinData([]);
      setLoadingRandomIsins(false);
    }
  };

  const fetchBondValuations = async () => {
    try {
      setLoadingBondValuations(true);
      const response = await apiClient.get(`${baseUrl}/yield/bond-valuations`, {
        timeout: 120000,
      });
      const result = response.data;
      let items = [];
      let tradeDate = null;

      if (result?.statusCode === 200 && result.data) {
        if (Array.isArray(result.data.valuations)) {
          items = result.data.valuations;
          tradeDate = result.data.tradeDate ?? null;
        } else if (Array.isArray(result.data)) {
          items = result.data;
        }
      } else if (Array.isArray(result?.data?.valuations)) {
        items = result.data.valuations;
        tradeDate = result.data.tradeDate ?? null;
      } else if (Array.isArray(result?.data)) {
        items = result.data;
      } else if (Array.isArray(result)) {
        items = result;
      }

      setBondValuations(items);
      setBondValuationsTradeDate(tradeDate);
      setActiveBondSlideIndex(0);
    } catch (error) {
      console.error('Error fetching bond valuations:', error);
      setBondValuations([]);
      setBondValuationsTradeDate(null);
    } finally {
      setLoadingBondValuations(false);
    }
  };

  const fetchTodayIssues = async () => {
    try {
      setLoadingTodayIssues(true);
      const response = await apiClient.get(`${baseUrl}/master-isin/latest-issues`);
      const result = response.data;
      let items = [];

      if (result?.statusCode === 200 && Array.isArray(result.data)) {
        items = result.data;
      } else if (Array.isArray(result?.data)) {
        items = result.data;
      } else if (Array.isArray(result)) {
        items = result;
      }

      setTodayIssuesData(items);
    } catch (error) {
      console.error('Error fetching today issues:', error);
      setTodayIssuesData([]);
    } finally {
      setLoadingTodayIssues(false);
    }
  };

  const formatYieldPercent = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    return `${num.toFixed(2)}%`;
  };

  const formatSpreadBps = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '—';
    const rounded = Math.round(num);
    const sign = rounded > 0 ? '+' : '';
    return `${sign}${rounded} bps`;
  };

  const getValuationBadge = (status) => {
    switch (status) {
      case 'UNDERVALUED':
        return { className: 'undervalued', label: 'UNDERVALUED' };
      case 'OVERVALUED':
        return { className: 'overvalued', label: 'OVERVALUED' };
      case 'FAIR_VALUE':
      default:
        return { className: 'fair', label: 'FAIR VALUE' };
    }
  };

  const getLiquidityFilledDots = (scanScore) => {
    const score = Number(scanScore);
    if (!Number.isFinite(score)) return 2;
    if (score >= 75) return 4;
    if (score >= 50) return 3;
    if (score >= 25) return 2;
    return 1;
  };

  const fetchRiskIntelligence = async () => {
    try {
      setLoadingRiskIntelligence(true);
      const response = await apiClient.get(`${baseUrl}/credit-risk/merton/risk-intelligence`);
      const result = response.data;
      let items = [];

      if (result?.statusCode === 200 && Array.isArray(result.data)) {
        items = result.data;
      } else if (Array.isArray(result?.data)) {
        items = result.data;
      } else if (Array.isArray(result)) {
        items = result;
      }
      setRiskIntelligenceData(items);
      setActiveRiskIndex(0);
    } catch (error) {
      console.error('Error fetching risk intelligence:', error);
      setRiskIntelligenceData([]);
    } finally {
      setLoadingRiskIntelligence(false);
    }
  };

  useEffect(() => {
    if (randomIsinData.length <= 1 || scanModalOpen || searchedIsinItem || assessmentCarouselHoverPaused) return undefined;

    const timer = setInterval(() => {
      setSlideDirection(1);
      setActiveIsinIndex((prev) => (prev + 1) % randomIsinData.length);
    }, 4500);

    return () => clearInterval(timer);
  }, [randomIsinData.length, scanModalOpen, searchedIsinItem, assessmentCarouselHoverPaused]);

  useEffect(() => {
    if (riskIntelligenceData.length <= 1 || riskCarouselPaused) return undefined;

    const timer = setInterval(() => {
      setRiskSlideDirection(1);
      setActiveRiskIndex((prev) => (prev + 1) % riskIntelligenceData.length);
    }, 5500);

    return () => clearInterval(timer);
  }, [riskIntelligenceData.length, riskCarouselPaused]);


  const goToRiskSlide = (idx) => {
    setRiskSlideDirection(idx > activeRiskIndex ? 1 : -1);
    setActiveRiskIndex(idx);
  };

  const goToNextRiskSlide = () => {
    if (riskIntelligenceData.length <= 1) return;
    setRiskSlideDirection(1);
    setActiveRiskIndex((prev) => (prev + 1) % riskIntelligenceData.length);
  };

  const goToPrevRiskSlide = () => {
    if (riskIntelligenceData.length <= 1) return;
    setRiskSlideDirection(-1);
    setActiveRiskIndex((prev) => (prev - 1 + riskIntelligenceData.length) % riskIntelligenceData.length);
  };

  const bondValuationSlides = useMemo(() => {
    const slides = [];
    for (let i = 0; i < bondValuations.length; i += BOND_VALUATION_CARDS_PER_SLIDE) {
      slides.push(bondValuations.slice(i, i + BOND_VALUATION_CARDS_PER_SLIDE));
    }
    return slides;
  }, [bondValuations]);

  const goToBondSlide = (idx) => {
    setBondSlideDirection(idx > activeBondSlideIndex ? 1 : -1);
    setActiveBondSlideIndex(idx);
  };

  const goToNextBondSlide = () => {
    if (bondValuationSlides.length <= 1) return;
    setBondSlideDirection(1);
    setActiveBondSlideIndex((prev) => (prev + 1) % bondValuationSlides.length);
  };

  const goToPrevBondSlide = () => {
    if (bondValuationSlides.length <= 1) return;
    setBondSlideDirection(-1);
    setActiveBondSlideIndex((prev) => (prev - 1 + bondValuationSlides.length) % bondValuationSlides.length);
  };

  const formatTradeDate = (dateStr) => {
    if (!dateStr) return 'latest available date';
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    if (bondValuationSlides.length <= 1 || bondCarouselHoverPaused) return undefined;

    const timer = setInterval(() => {
      setBondSlideDirection(1);
      setActiveBondSlideIndex((prev) => (prev + 1) % bondValuationSlides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [bondValuationSlides.length, bondCarouselHoverPaused]);

  const mapIsinDetailsToCardItem = (details, scanDetails) => {
    const creditRating = details?.creditRatings?.find((rating) => rating?.currentRating)?.currentRating
      || details?.creditRatings?.[0]?.currentRating
      || null;

    return {
      issuerName: details?.companyIssuerName,
      isin: details?.isin,
      couponRate: details?.couponRate,
      maturityDate: details?.dateOfRedemption || details?.redemption,
      creditRating,
      scanScore: scanDetails?.totalWeightedScore ?? null,
      scanDetails: scanDetails ?? null,
    };
  };

  const clearIsinSearch = () => {
    setIsinSearchQuery('');
    setSearchedIsinItem(null);
    setIsinSearchError('');
  };

  const closeSearchedIsinCard = () => {
    setSearchedIsinItem(null);
    setIsinSearchError('');
  };

  const handleIsinSearch = async (value) => {
    const isin = String(value ?? isinSearchQuery).trim().toUpperCase();
    if (!isin) {
      setIsinSearchError('Please enter an ISIN');
      return;
    }

    setIsinSearchLoading(true);
    setIsinSearchError('');
    setSearchedIsinItem(null);

    try {
      const detailsRes = await apiClient.get(`${baseUrl}/master-isin/details/${encodeURIComponent(isin)}`);
      const result = detailsRes.data;

      if (result?.statusCode !== 200 || !result?.data) {
        setIsinSearchError('ISIN not found');
        return;
      }

      let scanDetails = null;
      try {
        const scanRes = await apiClient.get(`${baseUrl}/scan/details/${encodeURIComponent(isin)}`, {
          timeout: 10000,
        });
        scanDetails = scanRes.data ?? null;
      } catch {
        scanDetails = null;
      }

      setSearchedIsinItem(mapIsinDetailsToCardItem(result.data, scanDetails));
      setSlideDirection(1);
    } catch (error) {
      console.error('Error searching ISIN:', error);
      setIsinSearchError('ISIN not found or unable to fetch details');
    } finally {
      setIsinSearchLoading(false);
    }
  };

  const getScanScoreStyle = (score) => {
    if (score == null || Number.isNaN(score)) {
      return { valueClass: '', label: 'N/A', stroke: '#d1d5db', percent: 0 };
    }
    if (score <= 40) {
      return { valueClass: 'low', label: score.toFixed(1), stroke: '#dc2626', percent: score };
    }
    if (score <= 70) {
      return { valueClass: 'mid', label: score.toFixed(1), stroke: '#ea580c', percent: score };
    }
    return { valueClass: 'high', label: score.toFixed(1), stroke: '#16a34a', percent: score };
  };

  const openScanScoreModal = async (item) => {
    if (!item?.isin) return;

    setScanModalIsin(item.isin);
    setScanModalOpen(true);

    if (item.scanDetails) {
      setScanModalData(item.scanDetails);
      setScanModalLoading(false);
      return;
    }

    setScanModalLoading(true);
    setScanModalData(null);
    try {
      const scanRes = await apiClient.get(`${baseUrl}/scan/details/${item.isin}`, {
        timeout: 10000,
      });
      setScanModalData(scanRes.data ?? null);
    } catch (error) {
      console.error('Error fetching scan score details:', error);
      setScanModalData(null);
    } finally {
      setScanModalLoading(false);
    }
  };

  const closeScanScoreModal = () => {
    setScanModalOpen(false);
    setScanModalData(null);
    setScanModalIsin('');
    setScanModalLoading(false);
  };

  const renderRandomIsinCard = useCallback((item, onScanScoreClick, options = {}) => {
    const { showClose = false, onClose } = options;
    const scanScore = item.scanScore != null
      ? Math.min(100, Math.max(0, Number(item.scanScore)))
      : null;
    const scanStyle = getScanScoreStyle(scanScore);
    const couponParts = parseCouponRateParts(item.couponRate);

    return (
      <div className="sb-indicator-card sb-assessment-isin-card">
        <div className="sb-assessment-isin-header">
          <div className="sb-assessment-isin-header-top">
            <span className="sb-assessment-isin-issuer" title={item.issuerName}>
              {item.issuerName || '—'}
            </span>
            {showClose && (
              <button
                type="button"
                className="sb-assessment-isin-close"
                onClick={onClose}
                aria-label="Close searched ISIN result"
                title="Close"
              >
                <CloseOutlined />
              </button>
            )}
          </div>
          <span className="sb-assessment-isin-code">{item.isin || '—'}</span>
        </div>

        <div
          className="sb-assessment-scan-hero"
          role="button"
          tabIndex={0}
          onClick={() => onScanScoreClick?.(item)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onScanScoreClick?.(item);
            }
          }}
          aria-label="View scan score details"
        >
          <div className="sb-assessment-scan-progress-wrap">
            <Progress
              type="circle"
              percent={scanStyle.percent}
              format={() => (
                <span className={`sb-assessment-scan-value ${scanStyle.valueClass}`}>
                  {scanStyle.label}
                </span>
              )}
              strokeColor={scanStyle.stroke}
              trailColor="rgba(15, 23, 42, 0.1)"
              size={160}
              strokeWidth={10}
              className="sb-assessment-scan-progress"
            />
          </div>
          <div className="sb-assessment-scan-label">Scan Score</div>
          <div className="sb-assessment-scan-hint">Comprehensive risk assessment</div>
          <div className="sb-assessment-scan-view">
            <EyeOutlined /> View Details
          </div>
        </div>

        <div className="sb-assessment-isin-details">
          <div className="sb-assessment-detail-row sb-assessment-coupon-row">
            <span>Coupon Rate</span>
            <span className="sb-assessment-coupon-value">
              <span className="sb-assessment-coupon-num">{couponParts.number}</span>
              {couponParts.showPercent && (
                <span className="sb-assessment-coupon-pct">%</span>
              )}
            </span>
          </div>
          <div className="sb-assessment-detail-row">
            <span>Credit Rating</span>
            <span className="sb-ind-rating-badge">{item.creditRating || 'N/A'}</span>
          </div>
          <div className="sb-assessment-detail-row">
            <span>Maturity</span>
            <span>{formatMaturityDate(item.maturityDate)}</span>
          </div>
        </div>
      </div>
    );
  }, []);

  const formatCategoryKey = (categoryKey) => {
    if (!categoryKey) return '';
    return String(categoryKey).replace(/_/g, ' ');
  };

  const riskInfoPopupContainer = (trigger) => trigger.closest('.sb-rie-carousel-shell') || document.body;

  const renderRiskIntelligenceCard = useCallback((item) => (
    <DefaultRiskPredictor
      data={item}
      getInfoPopupContainer={riskInfoPopupContainer}
      onInfoOpenChange={handleRiskInfoOpenChange}
    />
  ), []);

  const fetchMarketCapTickerData = async () => {
    try {
      // Using the absolute baseUrl from environment variables instead of relative proxy path
      const response = await apiClient.get(`${baseUrl}/master-issuer/issuers/latest-two-marketcaps`, { 
        withCredentials: false 
      });
      const result = response.data;
      
      if (result.statusCode === 200 && Array.isArray(result.data)) {
        // Filter out nulls and take a reasonable subset for the ticker
        const validData = result.data.filter(item => item.latestMarketCap !== null && item.olderMarketCap !== null);
        setMarketCapTickerData(validData);
      }
    } catch (error) {
      console.error('Error fetching Market Cap Ticker data:', error);
    }
  };

  useEffect(() => {
    fetchIssuersData();
    fetchIsinsData();
    fetchCurrentRatesData();
    fetchMarketCapTickerData();
    fetchRandomIsinData();
    fetchRiskIntelligence();
    fetchBondValuations();
    fetchTodayIssues();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const istTime = currentTime.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata'
  });

  const currentAssessmentItem = searchedIsinItem || randomIsinData[activeIsinIndex];
  const showAssessmentCarouselDots = !searchedIsinItem && randomIsinData.length > 1;

  // Animation Variants
  const fadeInUp = {
    initial: { y: 40, opacity: 0 },
    whileInView: { y: 0, opacity: 1 },
    viewport: { once: true },
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.2,
      },
    },
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemFadeInUp = {
    initial: { y: 20, opacity: 0 },
    whileInView: { y: 0, opacity: 1 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  };


  return (
    <LandingLayout>
      <div className="sb-landing-page">
        
        {/* 1. HERO SECTION */}
        <section className="sb-hero">
          <div className="sb-hero-bg-blobs">
            <div className="sb-hero-blob sb-hero-blob-1"></div>
            <div className="sb-hero-blob sb-hero-blob-2"></div>
          </div>
          <div className="sb-container sb-hero-centered">
            <div className="sb-hero-content-center">
              <motion.div 
                className="sb-hero-badge"
                initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
                animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <polyline points="9 12 11 14 15 10"></polyline>
                </svg>
                BOND WITH THE BEST
              </motion.div>
              
              <h1 className="sb-title-xl sb-hero-title">
                {["Simply", "Fixed", "Income."].map((word, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ 
                      duration: 1.2, 
                      delay: 0.2 + (i * 0.1),
                      ease: [0.21, 1.11, 0.81, 0.99]
                    }}
                    style={{ marginRight: '0.3em' }}
                  >
                    {word}
                  </motion.span>
                ))}
                <br />
                {["Nothing", "Else."].map((word, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ 
                      duration: 1.2, 
                      delay: 0.6 + (i * 0.1),
                      ease: [0.21, 1.11, 0.81, 0.99]
                    }}
                    style={{ color: '#c4d2d9', marginRight: '0.3em' }}
                  >
                    {word}
                  </motion.span>
                ))}
              </h1>

              <motion.p 
                className="sb-subtitle sb-hero-subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
              >
              Credible Ecosystem Powered by AI providing Comprehensive Fixed Income Solutions.
              </motion.p>

              <motion.div 
                className="sb-hero-actions"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 1, ease: "easeOut" }}
              >
                <Link to="/signup-modern" className="sb-btn sb-btn-dark">Start Investing</Link>
                <Link to="/bond-categories" className="sb-btn sb-btn-outline">View Collections</Link>
              </motion.div>
            </div>
          </div>
        </section>


        {/* 2. SERIOUS CAPITAL SECTION */}
        <section className="sb-serious-capital sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <h2 className="sb-serious-title">Designed for Diversified Investors.</h2>
              <p className="sb-serious-subtitle">
                Whether you're managing family wealth or institutional mandates, SimplyFI scales to your analytical depth.
              </p>
            </motion.div>



            <motion.div 
              className="sb-serious-grid"
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
            >
              
              <motion.div className="sb-serious-card" variants={itemFadeInUp}>
                <div className="sb-serious-icon-container">
                  <TeamOutlined className="sb-serious-icon" />
                </div>
                <h3 className="sb-serious-card-title">Retail</h3>
                <p className="sb-serious-card-text">
                  Secure, secondary market access with institutional quality risk assessment.
                </p>
                <ul className="sb-serious-feature-list">
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Easy Execution
                  </li>
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Risk Ratings
                  </li>
                </ul>
              </motion.div>

              <motion.div className="sb-serious-card" variants={itemFadeInUp}>
                <div className="sb-serious-icon-container">
                  <IdcardOutlined className="sb-serious-icon" />
                </div>
                <h3 className="sb-serious-card-title">HNI</h3>
                <p className="sb-serious-card-text">
                  Tax-efficient laddering and curated high-yield opportunities with simplified reporting.
                </p>
                <ul className="sb-serious-feature-list">
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Tax-Adjusted Yields
                  </li>
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Curated Offerings
                  </li>
                </ul>
              </motion.div>

              <motion.div className="sb-serious-card" variants={itemFadeInUp}>
                <div className="sb-serious-icon-container">
                  <ShopOutlined className="sb-serious-icon" />
                </div>
                <h3 className="sb-serious-card-title">Non-Institutional</h3>
                <p className="sb-serious-card-text">
                  High-liquidity strategies with primary access and secondary market depth.
                </p>
                <ul className="sb-serious-feature-list">
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Direct Market Access
                  </li>
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Liquidity Analysis
                  </li>
                </ul>
              </motion.div>

              <motion.div className="sb-serious-card" variants={itemFadeInUp}>
                <div className="sb-serious-icon-container">
                  <BankOutlined className="sb-serious-icon" />
                </div>
                <h3 className="sb-serious-card-title">Institutional</h3>
                <p className="sb-serious-card-text">
                  Deep liquidity pools, API access, and advanced portfolio stress testing tools.
                </p>
                <ul className="sb-serious-feature-list">
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Direct Bid/Offer
                  </li>
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Custom APIs
                  </li>
                </ul>
              </motion.div>

              <motion.div className="sb-serious-card sb-serious-card--domestic" variants={itemFadeInUp}>
                <div className="sb-serious-icon-container">
                  <UserOutlined className="sb-serious-icon" />
                </div>
                <h3 className="sb-serious-card-title">NRI</h3>
                <p className="sb-serious-card-text">
                  Bond market access tailored for Non-Resident Indians with compliant repatriation and reporting.
                </p>
                <ul className="sb-serious-feature-list">
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> NRE/NRO Compliance
                  </li>
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Repatriation Support
                  </li>
                </ul>
              </motion.div>

              <motion.div className="sb-serious-card sb-serious-card--domestic" variants={itemFadeInUp}>
                <div className="sb-serious-icon-container">
                  <GlobalOutlined className="sb-serious-icon" />
                </div>
                <h3 className="sb-serious-card-title">FPI</h3>
                <p className="sb-serious-card-text">
                  Cross-border regulatory compliance and currency-hedged yield analysis.
                </p>
                <ul className="sb-serious-feature-list">
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> FEMA Compliance
                  </li>
                  <li className="sb-serious-feature-item">
                    <CheckCircleOutlined className="sb-serious-check" /> Multi-Currency
                  </li>
                </ul>
              </motion.div>

            </motion.div>

          </div>
        </section>

        {/* 3. RBI CURRENT RATES SECTION */}
        <section className="sb-market sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <h2 className="sb-title-lg">
                Official RBI & FBIL benchmarks,<br />
                structured for decision-ready use.
              </h2>
              <p className="sb-subtitle">
                Policy rates from the Reserve Bank of India and market benchmark curves from FBIL —
                refreshed continuously into SimplyFI.
              </p>
            </motion.div>

            <motion.div
              className="sb-rates-split"
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true }}
            >
              <motion.div className="sb-rates-split__rbi" variants={itemFadeInUp}>
                <div className="sb-mi-header">
                  <h3 className="sb-mi-title">RBI Current Rates</h3>
                </div>

                {loadingCurrentRates ? (
                  <div className="sb-rbi-shell sb-rbi-shell--split">
                    <Spin size="large" />
                  </div>
                ) : currentRatesView.tabs.length === 0 ? (
                  <div className="sb-rbi-shell sb-rbi-shell--split">
                    No RBI current rates available.
                  </div>
                ) : (
                  <Collapse
                    accordion
                    bordered={false}
                    expandIconPosition="end"
                    className="sb-rbi-accordion"
                    activeKey={activeRateTab || undefined}
                    onChange={(key) => {
                      const next = Array.isArray(key) ? key[0] : key;
                      setActiveRateTab(next || '');
                    }}
                    items={currentRatesView.tabs.map((tab) => {
                      const panels =
                        tab.id === 'market_trends'
                          ? currentRatesView.marketSubs
                          : currentRatesView.bySection[tab.id]
                            ? [currentRatesView.bySection[tab.id]]
                            : [];

                      return {
                        key: tab.id,
                        label: tab.label,
                        children:
                          panels.length === 0 ? (
                            <div className="sb-rbi-acc-empty">No rates in this category.</div>
                          ) : (
                            panels.map((panel, panelIdx) => (
                              <div
                                key={`${tab.id}|${panel.subsection || ''}|${panelIdx}`}
                                className="sb-rbi-acc-block"
                              >
                                {tab.id === 'market_trends' && panel.subsection ? (
                                  <div className="sb-rbi-acc-subhead">
                                    {formatSectionLabel(panel.section, panel.subsection)}
                                  </div>
                                ) : null}
                                {panel.note ? (
                                  <p className="sb-rbi-acc-note">{panel.note}</p>
                                ) : null}
                                <div className="sb-rbi-acc-rows">
                                  {(panel.rates || []).map((rate, rateIdx) => (
                                    <div
                                      className="sb-rbi-acc-row"
                                      key={`${rate.rateKey}-${rateIdx}`}
                                    >
                                      <span className="sb-rbi-acc-key">{rate.rateKey}</span>
                                      <span className="sb-rbi-acc-value">
                                        : {rate.rateValue}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))
                          ),
                      };
                    })}
                  />
                )}
              </motion.div>

              <motion.div className="sb-rates-split__fbil" variants={itemFadeInUp}>
                <div className="sb-mi-header">
                  <h3 className="sb-mi-title">FBIL Benchmark Rates</h3>
                </div>
                <FbilBenchmarksPanel />
              </motion.div>
            </motion.div>

            <motion.div 
              className="sb-ticker-container"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7, duration: 1.2 }}
            >
              <div className="sb-ticker-header">
                <div><span className="sb-ticker-dot"></span>LIVE MARKET CAP TERMINAL</div>
                <div>IST {istTime}</div>
              </div>
              <div className="sb-ticker-wrap">
                <div className="sb-ticker">
                  {marketCapTickerData.length > 0 ? (
                    <>
                      {marketCapTickerData.map((item, idx) => {
                        const change = item.latestMarketCap - item.olderMarketCap;
                        const isUp = change >= 0;
                        return (
                          <div className="sb-ticker-item" key={idx}>
                            <span className={`sb-ticker-tag ${isUp ? '' : 'red'}`} style={{ fontSize: '1.2rem', padding: '4px 8px' }}>
                              {isUp ? <RiseOutlined /> : <FallOutlined />}
                            </span>
                            <span className="sb-ticker-text">
                              {item.issuerName}: ₹{item.latestMarketCap.toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr 
                              <span style={{ color: isUp ? '#10b981' : '#ef4444', marginLeft: '8px' }}>
                                ({isUp ? '+' : ''}{change.toFixed(2)})
                              </span>
                            </span>
                          </div>
                        );
                      })}
                      {/* Duplicate for infinite loop */}
                      {marketCapTickerData.map((item, idx) => {
                        const change = item.latestMarketCap - item.olderMarketCap;
                        const isUp = change >= 0;
                        return (
                          <div className="sb-ticker-item" key={`dup-${idx}`}>
                            <span className={`sb-ticker-tag ${isUp ? '' : 'red'}`} style={{ fontSize: '1.2rem', padding: '4px 8px' }}>
                              {isUp ? <RiseOutlined /> : <FallOutlined />}
                            </span>
                            <span className="sb-ticker-text">
                              {item.issuerName}: ₹{item.latestMarketCap.toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr 
                              <span style={{ color: isUp ? '#10b981' : '#ef4444', marginLeft: '8px' }}>
                                ({isUp ? '+' : ''}{change.toFixed(2)})
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    // Fallback while loading
                    <div className="sb-ticker-item">
                      <span className="sb-ticker-tag">SYNCING</span>
                      <span className="sb-ticker-text">Fetching latest market cap trends...</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. YIELD CURVE SECTION */}
        <section className="sb-yield-curve sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <h2 className="sb-title-lg">Benchmark yields critical for pricing of risk in<br/>diversified fixed income Portfolio</h2>
              
              <div className="sb-yc-header sb-yc-header--centered">
                <div>
                  <h3 className="sb-yc-title">Updated Yield Analytics</h3>
                  <p className="sb-yc-desc">G-Sec, SDL, and Risk-Adjusted Yields for Various Corporate Securities.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="sb-yc-cards-row"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              {YIELD_CURVE_SLIDES.map(({ key, Component, chartProps = {} }) => (
                <div key={key} className="sb-yc-card-item">
                  <Component showTrades={false} {...chartProps} />
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* 5. ASSESSMENT FRAMEWORK */}
        <section className="sb-assessment sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <h2 className="sb-title-lg">Comprehensive Risk Assessment Framework</h2>
              <p className="sb-subtitle" style={{margin:'0 auto'}}>
                Dynamic Framework to evalute Comprehensive Risk in the Fixed Income Instruments (ISIN wise).
              </p>
            </motion.div>

            <motion.div 
              className="sb-assessment-card"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
            >
              <div>
                <h3>Security Comprehensive Assesment Number-(SCAN)</h3>
                <p>
                Our proprietary risk assessment model providing SCAN score evaluates fixed income instruments across multiple dimensions to ensure safety and returns. 
                We analyze various factors covering financial health, management quality, instrument offering details and market dynamics.
                </p>
                <ul className="sb-check-list">
                  <motion.li initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 0.8 }}><CheckCircleOutlined className="sb-check-icon"/> Credit Rating Analysis</motion.li>
                  <motion.li initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.7, duration: 0.8 }}><CheckCircleOutlined className="sb-check-icon"/> Governance Parameters     </motion.li>
                  <motion.li initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.9, duration: 0.8 }}><CheckCircleOutlined className="sb-check-icon"/> Instrument Offering Details</motion.li>
                  <motion.li initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.9, duration: 0.8 }}><CheckCircleOutlined className="sb-check-icon"/> Market Activity</motion.li>
                </ul>
              </div>
              <div className="sb-assessment-carousel-wrap">
                <div className="sb-assessment-isin-search">
                  <p style={{ margin: '0 0 0.5rem', color: '#d1d5db', fontSize: '0.85rem' }}>
                    Enter any bond ISIN to view its risk scan score and factor-level breakdown.
                  </p>
                  <Search
                    placeholder="Enter bond ISIN (e.g., INE001A01036)"
                    value={isinSearchQuery}
                    onChange={(event) => {
                      const value = event.target.value.toUpperCase();
                      setIsinSearchQuery(value);
                      if (isinSearchError) setIsinSearchError('');
                      if (!value.trim()) {
                        setSearchedIsinItem(null);
                      }
                    }}
                    onSearch={handleIsinSearch}
                    loading={isinSearchLoading}
                    allowClear
                    onClear={clearIsinSearch}
                    enterButton={<><SearchOutlined /> Run Risk Scan</>}
                    size="large"
                    className="sb-assessment-isin-search-input"
                  />
                  {isinSearchError && (
                    <div className="sb-assessment-isin-search-error">{isinSearchError}</div>
                  )}
                </div>

                {loadingRandomIsins && !searchedIsinItem && !isinSearchLoading ? (
                  <div className="sb-assessment-carousel-loading">Loading risk assessment samples...</div>
                ) : isinSearchLoading ? (
                  <div className="sb-assessment-carousel-loading">Fetching ISIN scan score...</div>
                ) : currentAssessmentItem ? (
                  <div
                    className="sb-assessment-carousel"
                    onMouseEnter={() => setAssessmentCarouselHoverPaused(true)}
                    onMouseLeave={() => setAssessmentCarouselHoverPaused(false)}
                  >
                    <div className="sb-assessment-carousel-viewport">
                      <AnimatePresence mode="wait" custom={slideDirection}>
                        <motion.div
                          key={currentAssessmentItem.isin || 'assessment-card'}
                          className="sb-assessment-carousel-slide"
                          custom={slideDirection}
                          variants={assessmentSlideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        >
                          {renderRandomIsinCard(currentAssessmentItem, openScanScoreModal, {
                            showClose: Boolean(searchedIsinItem),
                            onClose: closeSearchedIsinCard,
                          })}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                    {showAssessmentCarouselDots && (
                      <div className="sb-assessment-carousel-dots" role="tablist" aria-label="Random ISIN samples">
                        {randomIsinData.map((item, idx) => (
                          <button
                            key={item.isin || idx}
                            type="button"
                            className={`sb-assessment-dot${idx === activeIsinIndex ? ' active' : ''}`}
                            aria-label={`Show ISIN ${idx + 1}`}
                            aria-selected={idx === activeIsinIndex}
                            onClick={() => goToAssessmentSlide(idx)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="sb-assessment-carousel-loading">No sample ISINs available.</div>
                )}
              </div>
            </motion.div>
          </div>
        </section>


        {/* 5B. RISK INTELLIGENCE ENGINE */}
        <section className="sb-risk-intelligence sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <div className="sb-rie-header">
                <span className="sb-rie-eyebrow">Risk Intelligence Engine Powered by Machine Learning and AI</span>
                <h2 className="sb-title-lg sb-rie-title">
                  Dynamic credit signals beyond ratings and market yields.
                </h2>
                <p className="sb-subtitle sb-rie-subtitle">
                  Forward-looking issuer credit view — risk band from default probability, with trend history for context.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="sb-rie-carousel-shell"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={() => setRiskCarouselHoverPaused(true)}
              onMouseLeave={() => setRiskCarouselHoverPaused(false)}
            >
              {loadingRiskIntelligence ? (
                <div className="sb-rie-loading">
                  <Spin size="large" />
                  <span>Loading structural credit intelligence...</span>
                </div>
              ) : riskIntelligenceData.length > 0 ? (
                <>
                  <div className="sb-rie-carousel-main">
                    {riskIntelligenceData.length > 1 && (
                      <button
                        type="button"
                        className="sb-rie-nav sb-rie-nav-prev"
                        aria-label="Previous issuer"
                        onClick={goToPrevRiskSlide}
                      >
                        <LeftOutlined />
                      </button>
                    )}

                    <div className="sb-rie-carousel-viewport">
                      <AnimatePresence mode="wait" custom={riskSlideDirection}>
                        <motion.div
                          key={riskIntelligenceData[activeRiskIndex]?.categoryKey || riskIntelligenceData[activeRiskIndex]?.issuerName || activeRiskIndex}
                          className="sb-rie-carousel-slide"
                          custom={riskSlideDirection}
                          variants={riskSlideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        >
                          {renderRiskIntelligenceCard(riskIntelligenceData[activeRiskIndex])}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {riskIntelligenceData.length > 1 && (
                      <button
                        type="button"
                        className="sb-rie-nav sb-rie-nav-next"
                        aria-label="Next issuer"
                        onClick={goToNextRiskSlide}
                      >
                        <RightOutlined />
                      </button>
                    )}
                  </div>

                  {riskIntelligenceData.length > 1 && (
                    <div className="sb-rie-dots" role="tablist" aria-label="Risk intelligence">
                      {riskIntelligenceData.map((item, idx) => (
                        <Tooltip
                          key={item.categoryKey || item.issuerName || idx}
                          title={
                            <span>
                              {getDisplayRiskLabel(item)}
                              {getDisplayRiskRange(item) !== 'N/A' ? ` · ${getDisplayRiskRange(item)}` : ''}
                            </span>
                          }
                          placement="top"
                        >
                          <button
                            type="button"
                            className={`sb-rie-dot${idx === activeRiskIndex ? ' active' : ''}`}
                            aria-label={`Show ${formatCategoryKey(item.categoryKey) || `issuer ${idx + 1}`}`}
                            aria-selected={idx === activeRiskIndex}
                            onClick={() => goToRiskSlide(idx)}
                          />
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="sb-rie-loading">No structural credit intelligence available.</div>
              )}
            </motion.div>
          </div>
        </section>


        {/* 6. DIVERSIFIED BONDS */}
        <section className="sb-bonds sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <h2 className="sb-title-lg">Risk adjusted pricing for diversified fixed<br/>income instruments through the lens of SCAN</h2>
              <p className="sb-subtitle">
                SimplyFI evaluates bond pricing relative to benchmark yields, peer issuers, and duration with reference to its fair value.
              </p>
              {!loadingBondValuations && bondValuationsTradeDate && (
                <p className="sb-bonds-as-on-date">
                 Secondary Trades as on {formatTradeDate(bondValuationsTradeDate)}
                </p>
              )}
            </motion.div>

            <motion.div
              key={loadingBondValuations ? 'bonds-loading' : `bonds-${bondValuations.map((valuation) => valuation.isin).join('-')}`}
              className="sb-bonds-carousel-shell"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              onMouseEnter={() => setBondCarouselHoverPaused(true)}
              onMouseLeave={() => setBondCarouselHoverPaused(false)}
            >
              {loadingBondValuations ? (
                <div className="sb-bonds-loading">
                  <Spin size="large" />
                </div>
              ) : bondValuations.length === 0 ? (
                <div className="sb-bonds-empty">
                  Bond valuations are unavailable right now.
                </div>
              ) : (
                <>
                  <div className="sb-bonds-carousel-main">
                    {bondValuationSlides.length > 1 && (
                      <button
                        type="button"
                        className="sb-bonds-nav sb-bonds-nav-prev"
                        aria-label="Previous bond valuations"
                        onClick={goToPrevBondSlide}
                      >
                        <LeftOutlined />
                      </button>
                    )}

                    <div className="sb-bonds-carousel-viewport">
                      <AnimatePresence mode="wait" custom={bondSlideDirection}>
                        <motion.div
                          key={`bond-slide-${activeBondSlideIndex}-${bondValuationSlides[activeBondSlideIndex]?.map((v) => v.isin).join('-') || activeBondSlideIndex}`}
                          className="sb-bonds-grid sb-bonds-carousel-slide"
                          custom={bondSlideDirection}
                          variants={riskSlideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        >
                          {(bondValuationSlides[activeBondSlideIndex] || []).map((valuation) => (
                            <div key={valuation.isin} className="sb-bonds-card-wrap">
                              <BondValuationCard valuation={valuation} />
                            </div>
                          ))}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {bondValuationSlides.length > 1 && (
                      <button
                        type="button"
                        className="sb-bonds-nav sb-bonds-nav-next"
                        aria-label="Next bond valuations"
                        onClick={goToNextBondSlide}
                      >
                        <RightOutlined />
                      </button>
                    )}
                  </div>

                  {bondValuationSlides.length > 1 && (
                    <div className="sb-bonds-dots" role="tablist" aria-label="Bond valuations">
                      {bondValuationSlides.map((slide, idx) => (
                        <button
                          key={`bond-dot-${slide.map((v) => v.isin).join('-') || idx}`}
                          type="button"
                          className={`sb-bonds-dot${idx === activeBondSlideIndex ? ' active' : ''}`}
                          aria-label={`Show bond valuations slide ${idx + 1}`}
                          aria-selected={idx === activeBondSlideIndex}
                          onClick={() => goToBondSlide(idx)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </div>
        </section>

        {/* 7. PORTFOLIO SECTION */}
        <section className="sb-portfolio sb-section">
          <div className="sb-container">
            <motion.div {...fadeInUp}>
              <h2 className="sb-title-lg">Construct a fixed income portfolio aligned with<br/>target yields, duration preferences, and risk appetite.</h2>
              <p className="sb-subtitle">
                Set your risk profile, horizon, and macro view — our engine allocates across G-Sec, SDL, PSU, NCD, and more.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <PortfolioBuilderShowcase />
            </motion.div>
          </div>
        </section>


        {/* 8. ISSUES SECTION */}
        <section className="sb-issues sb-section">
          <div className="sb-container">
            <motion.h2 className="sb-issues-title-lg" {...fadeInUp}>
              Monitor bonds currently open for subscription<br/>and track upcoming issuances expected to<br/>enter the market.
            </motion.h2>


            {/* Block 1: Today's Issues */}
            <div className="sb-issues-block">
              {/* Left Column: Mini Cards */}
              <motion.div
                key={loadingTodayIssues ? 'today-issues-loading' : `today-issues-${todayIssuesData.map((issue) => issue.isin).join('-')}`}
                className="sb-issues-cards"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                {loadingTodayIssues ? (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <Spin />
                  </div>
                ) : todayIssuesData.length === 0 ? (
                  <div className="sb-issues-empty" style={{ textAlign: 'center', padding: '32px 0' }}>
                    No new issues available right now.
                  </div>
                ) : (
                  todayIssuesData.map((issue) => (
                    <motion.div
                      key={issue.isin}
                      variants={itemFadeInUp}
                      className="sb-issues-card-wrap"
                    >
                      <TodayIssueCard issue={issue} />
                    </motion.div>
                  ))
                )}
              </motion.div>


              {/* Right Column: Text */}
              <motion.div 
                className="sb-issues-text-col"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2 }}
              >
                <h3>Today’s Issues</h3>
                <p>
                  Explore bond offerings currently open for subscription in the primary market. Review coupon rates, maturity profiles, credit ratings, and minimum investment requirements before placing bids. Real-time subscription indicators help assess demand and timing before the issuance window closes.
                </p>
              </motion.div>

            </div>

            {/* Block 2: Forthcoming Issues */}
            <div className="sb-issues-block" style={{ marginBottom: 0 }}>
              {/* Left Column: Text */}
              <motion.div 
                className="sb-issues-text-col"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2 }}
              >
                <h3>Forthcoming Issues</h3>
                <p>
                  Stay ahead of upcoming debt issuances expected to enter the market shortly. Monitor anticipated yield ranges, issuer quality, and issue structure in advance, allowing investors to plan allocations and set alerts before the subscription window opens.
                </p>
              </motion.div>


              {/* Right Column: Major Card */}
              <div>
                <div className="sb-major-card">
                  <div className="sb-major-header">
                    <span className="sb-major-label">NEXT MAJOR ISSUANCE</span>
                    <button className="sb-watchlist-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                      </svg>
                      Add to Watchlist
                    </button>
                  </div>
                  
                  <div className="sb-major-title">
                    HDFC Tier-1<br/>Issue <span>(Perp)</span>
                  </div>
                  
                  <div className="sb-major-yield">
                    Expected Yield: <span>8.25% - 8.40%</span>
                  </div>

                  <div className="sb-major-note">
                    Institutional allocation window opening soon. Set up alerts for automated bidding workflows.
                  </div>

                  <div className="sb-countdown-grid">
                    <div className="sb-countdown-box">
                      <span className="sb-countdown-val">02</span>
                      <span className="sb-countdown-label">DAYS</span>
                    </div>
                    <div className="sb-countdown-box">
                      <span className="sb-countdown-val">14</span>
                      <span className="sb-countdown-label">HOURS</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </section>

        {/* 9. CTA */}
        <section className="sb-cta">
          <motion.div 
            className="sb-container"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="sb-cta-eyebrow">"Fixed income should not be impulsive. It should be disciplined."</div>
            <h2 className="sb-cta-title">
              SimplyFI.<br/>
              <span>Bond with the Best.</span>
            </h2>
            <motion.button 
              className="sb-cta-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Request Private Access
            </motion.button>
            <div className="sb-cta-note">EXCLUSIVELY FOR QUALIFIED INVESTORS</div>
          </motion.div>
        </section>


      </div>

      <Modal
        title={`Scan Score Analysis${scanModalIsin ? ` — ${scanModalIsin}` : ''}`}
        open={scanModalOpen}
        onCancel={closeScanScoreModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <div className="scan-score-section">
          <Card className="scan-score-card">
            {scanModalLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spin size="large" />
                <div style={{ marginTop: '1rem', color: isDark ? '#ffffff' : '#000000' }}>
                  Loading scan score data...
                </div>
              </div>
            ) : scanModalData ? (
              <Row gutter={[24, 24]}>
                <Col xs={24} md={8}>
                  <div className="scan-score-overview">
                    <div className="scan-score-main">
                      <div className="scan-score-value">
                        <Text
                          style={{
                            fontSize: '72px',
                            fontWeight: 'bold',
                            color: isDark ? '#8b5cf6' : '#1890ff',
                          }}
                        >
                          {scanModalData.totalWeightedScore?.toFixed(1) || 'N/A'}
                        </Text>
                      </div>
                      <div className="scan-score-label">
                        <Text style={{ fontSize: '25px', color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#666', fontWeight: 'bolder' }}>
                          Total Score
                        </Text>
                      </div>
                    </div>
                    <div className="scan-score-description">
                      <Text type="secondary">
                        Comprehensive risk assessment based on financial
                        strength, governance, market activity, and
                        complexity factors.
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={16}>
                  <div className="scan-score-chart-container">
                    <ScanScoreChart scanData={scanModalData} isDark={isDark} />
                  </div>
                </Col>
              </Row>
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#64748b',
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: isDark ? '#ffffff' : '#000000',
                  }}
                >
                  No Scan Score Data Available
                </div>
                <div style={{ fontSize: '14px', color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#64748b' }}>
                  Scan score analysis is not available for this ISIN at the moment.
                </div>
              </div>
            )}
          </Card>
        </div>
      </Modal>
    </LandingLayout>
  );
};

export default LandingPageRedesign;
